import { useState, useEffect, useMemo } from "react";
import {
  Grid, Box, Tabs, Tab, Typography, Paper, Divider, Chip, Stack, Card, CardContent,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Switch,
  useTheme, useMediaQuery,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CajasProvider } from "src/context/CajasContext/CajasContext";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import BarChartIcon from "@mui/icons-material/BarChart";
import { ListTablePaginated } from "./ListTablePaginated";
import { getEstadisticasPaginated, getEstadisticas } from "src/general_helpers/EstadisticasData";
import { BloqueProceso } from "./BloqueProceso";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { GraficoMensual } from "./GraficoMensual";
import { RankingProcesos } from "./RankingProcesos";
import { GraficoDistribucion } from "./GraficoDistribucion";
import { GraficoPorUsuario } from "./GraficoPorUsuario";
import { RankingUsuarios } from "./RankingUsuarios";
import { AlertasSistema } from "./AlertasSistema";

// ─── KPI card inline ────────────────────────────────────────────────────────
const KpiCard = ({ label, value, Icon, color, sublabel }) => (
  <Card elevation={2} sx={{ borderRadius: 3, height: '100%', background: `linear-gradient(135deg, ${color}18, #fff)` }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
          <Typography variant="h4" fontWeight={800} color={color}>
            {typeof value === 'number' ? value.toLocaleString('es-AR') : value}
          </Typography>
          {sublabel && (
            <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}20`, borderRadius: 2, p: 1, flexShrink: 0 }}>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ─── Componente principal ────────────────────────────────────────────────────
export const EstadisticasList = () => {
  // Usuario
  const user_data = JSON.parse(localStorage.getItem("siam-user-data"));
  const userId = user_data?.id;
  const userName = user_data?.usuario || user_data?.nombre || user_data?.name || user_data?.email || `#${userId}`;
  const isAdmin = ['1', '2'].includes(String(user_data?.id_perfiles));

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mes actual
  const hoy = new Date();
  const mesActualLabel = hoy.toLocaleString('es-AR', { month: 'long', year: 'numeric' });
  const diasEnMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
  const diasRestantes = diasEnMes - hoy.getDate();
  const diasColor = diasRestantes <= 3 ? "error" : diasRestantes <= 7 ? "warning" : "success";

  // Filtro usuario
  const [soloMisDatos, setSoloMisDatos] = useState(true);

  // Tabla
  const listName = "Estadisticas";
  const columns = [
    { field: "id", headerName: "ID", flex: 0.3 },
    { field: "proceso", headerName: "Proceso", flex: 1 },
    { field: "id_cuentas", headerName: "Cuenta", flex: 0.6 },
    { field: "id_usuarios", headerName: "Usuario", flex: 0.6 },
    { field: "mes", headerName: "Mes", flex: 0.4 },
    { field: "ano", headerName: "Año", flex: 0.4 },
    { field: "cantidad", headerName: "Cantidad", flex: 0.6 },
  ];

  const [responseService, setResponseService] = useState(false);
  const [limites, setLimites] = useState({
    consultas: { usado: 0, permitido: 0, ilimitado: true },
    busquedas: { usado: 0, permitido: 0, ilimitado: true },
    descargas: { usado: 0, permitido: 0, ilimitado: true },
  });
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [view, setView] = useState("resumen");

  const [options, setOptions] = useState({
    cols: columns,
    get: getEstadisticas,
    getPaginated: getEstadisticasPaginated,
    pageSize: [5, 10, 25],
    limit: 50,
    filter: true,
    exportData: true,
    server: true,
    setResponseService: setResponseService,
    idElementData: { id_usuarios: userId },
  });

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      idElementData: soloMisDatos ? { id_usuarios: userId } : {},
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloMisDatos]);

  useEffect(() => {
    if (responseService?.total_records !== undefined) {
      setMessage("Se han encontrado " + responseService.total_records + " registro(s)");
      setSeverity("success");
      setSnackbarVisible(true);
      if (responseService.limites) setLimites(responseService.limites);
    }
  }, [responseService]);

  // ─── Datos derivados ─────────────────────────────────────────────────────
  const data = responseService?.data || [];

  const totales = useMemo(() => {
    const t = { consultas: 0, busquedas: 0, descargas: 0 };
    data.forEach(r => {
      const cant = parseInt(r.cantidad, 10) || 0;
      if (r.proceso === 'consultas') t.consultas += cant;
      else if (r.proceso === 'busquedas') t.busquedas += cant;
      else if (r.proceso === 'descargas') t.descargas += cant;
    });
    return t;
  }, [data]);

  const totalGeneral = totales.consultas + totales.busquedas + totales.descargas;

  return (
    <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
      <PageHeaderCard title={listName} icon={<BarChartIcon />} />

      <CajasProvider>
        <>
          {/* ─── BANNER DE USUARIO ─────────────────────────────────────── */}
          <Paper elevation={2} sx={{
            mb: 2, p: { xs: 1.5, sm: 2 }, borderRadius: 3,
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)", color: "#fff",
          }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PersonIcon fontSize="small" />
                  <Typography variant={isMobile ? "body2" : "body1"} fontWeight={600}>
                    {soloMisDatos ? `Mis estadísticas — ${userName}` : "Estadísticas globales"}
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.4)", display: { xs: "none", sm: "block" } }} />
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarTodayIcon fontSize="small" />
                  <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{mesActualLabel}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <AccessTimeIcon fontSize="small" />
                  <Chip
                    label={`${diasRestantes} días restantes`}
                    color={diasColor}
                    size="small"
                    sx={{ fontWeight: 600, "& .MuiChip-label": { color: "#fff" } }}
                  />
                </Stack>
              </Stack>
              {isAdmin && (
                <FormControlLabel
                  control={
                    <Switch checked={soloMisDatos} onChange={(e) => setSoloMisDatos(e.target.checked)}
                      size="small" sx={{ "& .MuiSwitch-thumb": { bgcolor: "#fff" } }} />
                  }
                  label={<Typography variant="body2" sx={{ color: "#fff" }}>Solo mis datos</Typography>}
                  sx={{ ml: 0, mr: 0 }}
                />
              )}
            </Stack>
          </Paper>

          {/* ─── ALERTAS ────────────────────────────────────────────────── */}
          <AlertasSistema limites={limites} />

          {/* ─── LÍMITES ─────────────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { titulo: "Consultas", info: limites.consultas },
              { titulo: "Búsquedas", info: limites.busquedas },
              { titulo: "Descargas", info: limites.descargas },
            ].map(({ titulo, info }) => (
              <Grid item xs={12} sm={4} key={titulo}>
                <BloqueProceso titulo={titulo} info={info} diasRestantes={diasRestantes} />
              </Grid>
            ))}
          </Grid>

          {/* ─── TABS ────────────────────────────────────────────────────── */}
          <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={view} onChange={(_, v) => setView(v)} variant="scrollable" scrollButtons="auto">
              <Tab value="resumen" label="Resumen" />
              <Tab value="mensual" label="Por mes" />
              <Tab value="distribucion" label="Distribución" />
            </Tabs>
          </Box>

          {/* ════════════════════════════════════════════════════════════════
              TAB: RESUMEN
          ════════════════════════════════════════════════════════════════ */}
          {view === "resumen" && (
            <Box>
              {/* KPI row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Consultas" value={totales.consultas} Icon={VisibilityIcon} color="#1976d2"
                    sublabel={totalGeneral > 0 ? `${Math.round((totales.consultas / totalGeneral) * 100)}% del total` : undefined} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Búsquedas" value={totales.busquedas} Icon={SearchIcon} color="#ed6c02"
                    sublabel={totalGeneral > 0 ? `${Math.round((totales.busquedas / totalGeneral) * 100)}% del total` : undefined} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Descargas" value={totales.descargas} Icon={FileDownloadIcon} color="#2e7d32"
                    sublabel={totalGeneral > 0 ? `${Math.round((totales.descargas / totalGeneral) * 100)}% del total` : undefined} />
                </Grid>
              </Grid>

              {/* Rankings + donut */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <RankingProcesos data={data} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <GraficoDistribucion data={data} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <RankingUsuarios data={data} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ════════════════════════════════════════════════════════════════
              TAB: POR MES
          ════════════════════════════════════════════════════════════════ */}
          {view === "mensual" && (
            <Box>
              <GraficoMensual data={data} />
            </Box>
          )}

          {/* ════════════════════════════════════════════════════════════════
              TAB: DISTRIBUCIÓN
          ════════════════════════════════════════════════════════════════ */}
          {view === "distribucion" && (
            <Box>
              {/* KPI del período */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Total consultas" value={totales.consultas} Icon={VisibilityIcon} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Total búsquedas" value={totales.busquedas} Icon={SearchIcon} color="#ed6c02" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Total descargas" value={totales.descargas} Icon={FileDownloadIcon} color="#2e7d32" />
                </Grid>
              </Grid>

              {/* Donut + barras de usuarios */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <GraficoDistribucion data={data} />
                </Grid>
                <Grid item xs={12} md={7}>
                  <GraficoPorUsuario data={data} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ─── TABLA ───────────────────────────────────────────────────── */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>Detalle de registros</Typography>
            <ListTablePaginated options={options} />
          </Box>
        </>
      </CajasProvider>

      <SnackbarMessenger
        open={snackbarVisible}
        message={message}
        severity={severity}
        hide={8000}
        close={() => setSnackbarVisible(false)}
      />
    </Grid>
  );
};
